import React from 'react';
import { Container, Grid, Box, Typography, Card, CardContent } from '@mui/material';
import { Calculate, MenuBook, Psychology, Extension } from '@mui/icons-material';

export const CoursesSection: React.FC = () => {
    const courses = [
        {
            icon: Calculate,
            title: '11+ Maths',
            features: [
                'Arithmetic, fractions, percentages, ratios',
                'Word problems and reasoning',
                'Time-saving exam strategies',
            ],
            description: 'Build strong mathematical foundations with problem-solving techniques that boost speed and accuracy.',
        },
        {
            icon: MenuBook,
            title: '11+ English',
            features: [
                'Comprehension skills',
                'Creative and persuasive writing',
                'Grammar, spelling, and vocabulary building',
            ],
            description: 'Develop excellent reading comprehension and writing skills through engaging texts and structured practice.',
        },
        {
            icon: Psychology,
            title: 'Verbal Reasoning',
            features: [
                'Logical thinking and pattern recognition',
                'Codes, sequences, and word relationships',
                'Accuracy and speed improvement',
            ],
            description: 'Master verbal logic puzzles and develop critical thinking skills essential for 11+ success.',
        },
        {
            icon: Extension,
            title: 'Non-Verbal Reasoning',
            features: [
                'Shape patterns and spatial awareness',
                'Visual problem-solving techniques',
                'Timed practice for exam confidence',
            ],
            description: 'Strengthen visual reasoning abilities through systematic practice with shapes, patterns, and sequences.',
        },
    ];

    return (
        <Box
            id="courses"
            sx={{
                py: { xs: 8, md: 12 },
            }}
        >
            <Container maxWidth="lg">
                <Typography
                    variant="h2"
                    sx={{
                        mb: 2,
                        textAlign: 'center',
                        background: 'linear-gradient(135deg, #5b4cdb 0%, #1e293b 100%)',
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent',
                    }}
                >
                    Our 11+ Courses
                </Typography>

                <Typography
                    variant="body1"
                    sx={{
                        mb: 6,
                        textAlign: 'center',
                        color: 'text.secondary',
                        maxWidth: 700,
                        mx: 'auto',
                        lineHeight: 1.8,
                    }}
                >
                    Comprehensive coverage of all four 11+ subjects with expert guidance,
                    structured practice, and proven exam techniques.
                </Typography>

                <Grid container spacing={3}>
                    {courses.map((course, index) => {
                        const Icon = course.icon;
                        return (
                            <Grid size={{ xs: 12, sm: 6 }} key={index}>
                                <Card
                                    sx={{
                                        height: '100%',
                                        transition: 'all 0.3s ease',
                                    }}
                                >
                                    <CardContent sx={{ p: 4 }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                                            <Box
                                                sx={{
                                                    width: 48,
                                                    height: 48,
                                                    borderRadius: 2,
                                                    background: 'linear-gradient(135deg, #5b4cdb 0%, #06b6d4 100%)',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                }}
                                            >
                                                <Icon sx={{ color: '#fff', fontSize: 24 }} />
                                            </Box>
                                            <Typography variant="h5" sx={{ fontWeight: 600 }}>
                                                {course.title}
                                            </Typography>
                                        </Box>

                                        <Typography
                                            variant="body2"
                                            sx={{
                                                mb: 2.5,
                                                color: 'text.secondary',
                                                lineHeight: 1.7,
                                                fontStyle: 'italic',
                                            }}
                                        >
                                            {course.description}
                                        </Typography>

                                        <Box component="ul" sx={{ pl: 2, m: 0 }}>
                                            {course.features.map((feature, idx) => (
                                                <Typography
                                                    component="li"
                                                    key={idx}
                                                    variant="body2"
                                                    sx={{ mb: 1.5, color: 'text.secondary', lineHeight: 1.7 }}
                                                >
                                                    {feature}
                                                </Typography>
                                            ))}
                                        </Box>
                                    </CardContent>
                                </Card>
                            </Grid>
                        );
                    })}
                </Grid>

                {/* Additional Info */}
                <Box
                    sx={{
                        mt: 6,
                        p: 4,
                        borderRadius: 3,
                        background: 'linear-gradient(135deg, rgba(91, 76, 219, 0.05) 0%, rgba(6, 182, 212, 0.05) 100%)',
                        border: '1px solid rgba(91, 76, 219, 0.15)',
                        textAlign: 'center',
                    }}
                >
                    <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                        Flexible Learning Options
                    </Typography>
                    <Typography variant="body1" sx={{ color: 'text.secondary', lineHeight: 1.8 }}>
                        Choose individual subjects or comprehensive packages. All courses include regular
                        assessments, mock exams, and detailed progress reports to track improvement.
                    </Typography>
                </Box>
            </Container>
        </Box>
    );
};
